/**
 * Well Check Tracking Service
 * Tracks pumper visits to wells with GPS verification
 * Provides accountability data for managers
 */

import { db } from '../firebase/client';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  Timestamp,
  onSnapshot,
  limit
} from 'firebase/firestore';
import { getCompanyId } from '../tenantContext';

export interface WellCheck {
  id?: string;
  companyId: string;
  wellId: string;
  wellName: string;
  wellApiNumber?: string;
  pumperId: string;
  pumperName: string;
  pumperEmail?: string;
  
  // Check-in details
  checkInTime: Date;
  checkInLocation: {
    lat: number;
    lng: number;
    accuracy: number; // meters
  };
  distanceFromWell: number; // feet at check-in
  
  // Check-out details (optional - may leave without explicit checkout)
  checkOutTime?: Date;
  checkOutLocation?: {
    lat: number;
    lng: number;
    accuracy: number;
  };
  durationMinutes?: number;
  
  // Verification
  verificationStatus: 'verified' | 'unverified' | 'suspicious';
  verificationMethod: 'gps_auto' | 'gps_manual' | 'manual_override';
  verificationNotes?: string;
  
  // Data entry linkage
  dataEntryCompleted: boolean;
  productionEntryId?: string;
  ticketsCreated?: string[];
  photosAdded?: number;
  
  // Route context
  routeId?: string;
  routeSequence?: number; // Position in route (1st well, 2nd well, etc.)
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

export interface WellCheckStats {
  wellId: string;
  wellName: string;
  totalChecks: number;
  checksToday: number;
  checksThisWeek: number;
  averageDuration: number; // minutes
  lastCheckTime?: Date;
  lastCheckedBy?: string;
  missedCheckDays: number; // Days without a check in last 7 days
}

export interface PumperCheckStats {
  pumperId: string;
  pumperName: string;
  totalChecksToday: number;
  totalChecksThisWeek: number;
  wellsVisitedToday: string[];
  averageTimePerWell: number; // minutes
  totalMilesToday: number;
  verifiedChecks: number;
  unverifiedChecks: number;
  suspiciousChecks: number;
}

class WellCheckService {
  private collectionName = 'well_checks';
  
  /**
   * Record a check-in at a well
   */
  async checkIn(data: {
    wellId: string;
    wellName: string;
    wellApiNumber?: string;
    wellLocation: { lat: number; lng: number };
    pumperId: string;
    pumperName: string;
    pumperEmail?: string;
    currentLocation: { lat: number; lng: number; accuracy: number };
    routeId?: string;
    routeSequence?: number;
  }): Promise<string> {
    const companyId = getCompanyId();
    
    // Calculate distance from well
    const distanceFeet = this.calculateDistanceFeet(
      data.currentLocation.lat,
      data.currentLocation.lng,
      data.wellLocation.lat,
      data.wellLocation.lng
    );
    
    // Determine verification status
    let verificationStatus: WellCheck['verificationStatus'] = 'verified';
    let verificationNotes = '';
    
    if (distanceFeet > 500) {
      verificationStatus = 'suspicious';
      verificationNotes = `Check-in from ${Math.round(distanceFeet)} feet away`;
    } else if (distanceFeet > 250) {
      verificationStatus = 'unverified';
      verificationNotes = `Check-in from ${Math.round(distanceFeet)} feet - within range but not at wellhead`;
    }
    
    const checkData: Omit<WellCheck, 'id'> = {
      companyId,
      wellId: data.wellId,
      wellName: data.wellName,
      wellApiNumber: data.wellApiNumber,
      pumperId: data.pumperId,
      pumperName: data.pumperName,
      pumperEmail: data.pumperEmail,
      checkInTime: new Date(),
      checkInLocation: data.currentLocation,
      distanceFromWell: distanceFeet,
      verificationStatus,
      verificationMethod: 'gps_auto',
      verificationNotes: verificationNotes || undefined,
      dataEntryCompleted: false,
      routeId: data.routeId,
      routeSequence: data.routeSequence,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    const docRef = await addDoc(collection(db, this.collectionName), {
      ...checkData,
      checkInTime: Timestamp.fromDate(checkData.checkInTime),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    
    
    return docRef.id;
  }
  
  /**
   * Record a check-out from a well
   */
  async checkOut(
    checkId: string,
    currentLocation: { lat: number; lng: number; accuracy: number },
    dataEntryCompleted: boolean = false,
    productionEntryId?: string,
    ticketsCreated?: string[],
    photosAdded?: number
  ): Promise<void> {
    const checkInTime = await this.getCheckInTime(checkId);
    const durationMinutes = checkInTime 
      ? Math.round((Date.now() - checkInTime.getTime()) / 60000)
      : undefined;
    
    await updateDoc(doc(db, this.collectionName, checkId), {
      checkOutTime: Timestamp.now(),
      checkOutLocation: currentLocation,
      durationMinutes,
      dataEntryCompleted,
      productionEntryId: productionEntryId || null,
      ticketsCreated: ticketsCreated || [],
      photosAdded: photosAdded || 0,
      updatedAt: Timestamp.now(),
    });
    
  }
  
  /**
   * Mark that data entry was completed for a check
   */
  async markDataEntryCompleted(
    checkId: string,
    productionEntryId: string
  ): Promise<void> {
    await updateDoc(doc(db, this.collectionName, checkId), {
      dataEntryCompleted: true,
      productionEntryId,
      updatedAt: Timestamp.now(),
    });
  }
  
  /**
   * Get check-in time for duration calculation
   */
  private async getCheckInTime(checkId: string): Promise<Date | null> {
    try {
      const docSnap = await getDocs(
        query(collection(db, this.collectionName), where('__name__', '==', checkId))
      );
      if (!docSnap.empty) {
        const data = docSnap.docs[0].data();
        return data.checkInTime?.toDate() || null;
      }
    } catch (error) {
      console.error('Error getting check-in time:', error);
    }
    return null;
  }
  
  /**
   * Get all checks for today (for manager dashboard)
   */
  async getTodaysChecks(): Promise<WellCheck[]> {
    const companyId = getCompanyId();
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    
    const q = query(
      collection(db, this.collectionName),
      where('companyId', '==', companyId),
      where('checkInTime', '>=', Timestamp.fromDate(startOfDay)),
      orderBy('checkInTime', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      checkInTime: doc.data().checkInTime?.toDate(),
      checkOutTime: doc.data().checkOutTime?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as WellCheck[];
  }
  
  /**
   * Get checks for a specific well
   */
  async getWellChecks(wellId: string, days: number = 7): Promise<WellCheck[]> {
    const companyId = getCompanyId();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const q = query(
      collection(db, this.collectionName),
      where('companyId', '==', companyId),
      where('wellId', '==', wellId),
      where('checkInTime', '>=', Timestamp.fromDate(startDate)),
      orderBy('checkInTime', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      checkInTime: doc.data().checkInTime?.toDate(),
      checkOutTime: doc.data().checkOutTime?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as WellCheck[];
  }
  
  /**
   * Get checks for a specific pumper
   */
  async getPumperChecks(pumperId: string, days: number = 7): Promise<WellCheck[]> {
    const companyId = getCompanyId();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const q = query(
      collection(db, this.collectionName),
      where('companyId', '==', companyId),
      where('pumperId', '==', pumperId),
      where('checkInTime', '>=', Timestamp.fromDate(startDate)),
      orderBy('checkInTime', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      checkInTime: doc.data().checkInTime?.toDate(),
      checkOutTime: doc.data().checkOutTime?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as WellCheck[];
  }
  
  /**
   * Get well check statistics for manager dashboard
   */
  async getWellCheckStats(wellIds: string[]): Promise<WellCheckStats[]> {
    const companyId = getCompanyId();
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(now);
    startOfWeek.setDate(startOfWeek.getDate() - 7);
    
    const stats: WellCheckStats[] = [];
    
    for (const wellId of wellIds) {
      const weekChecks = await this.getWellChecks(wellId, 7);
      const todayChecks = weekChecks.filter(c => 
        c.checkInTime && c.checkInTime >= startOfDay
      );
      
      // Calculate average duration
      const checksWithDuration = weekChecks.filter(c => c.durationMinutes);
      const avgDuration = checksWithDuration.length > 0
        ? checksWithDuration.reduce((sum, c) => sum + (c.durationMinutes || 0), 0) / checksWithDuration.length
        : 0;
      
      // Count days with checks in last 7 days
      const daysWithChecks = new Set(
        weekChecks.map(c => c.checkInTime?.toDateString())
      ).size;
      
      const lastCheck = weekChecks[0];
      
      stats.push({
        wellId,
        wellName: lastCheck?.wellName || wellId,
        totalChecks: weekChecks.length,
        checksToday: todayChecks.length,
        checksThisWeek: weekChecks.length,
        averageDuration: Math.round(avgDuration),
        lastCheckTime: lastCheck?.checkInTime,
        lastCheckedBy: lastCheck?.pumperName,
        missedCheckDays: 7 - daysWithChecks,
      });
    }
    
    return stats;
  }
  
  /**
   * Get pumper statistics for manager dashboard
   */
  async getPumperStats(pumperIds: string[]): Promise<PumperCheckStats[]> {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(now);
    startOfWeek.setDate(startOfWeek.getDate() - 7);
    
    const stats: PumperCheckStats[] = [];
    
    for (const pumperId of pumperIds) {
      const weekChecks = await this.getPumperChecks(pumperId, 7);
      const todayChecks = weekChecks.filter(c => 
        c.checkInTime && c.checkInTime >= startOfDay
      );
      
      // Calculate stats
      const wellsToday = [...new Set(todayChecks.map(c => c.wellId))];
      const checksWithDuration = weekChecks.filter(c => c.durationMinutes);
      const avgTime = checksWithDuration.length > 0
        ? checksWithDuration.reduce((sum, c) => sum + (c.durationMinutes || 0), 0) / checksWithDuration.length
        : 0;
      
      const verified = weekChecks.filter(c => c.verificationStatus === 'verified').length;
      const unverified = weekChecks.filter(c => c.verificationStatus === 'unverified').length;
      const suspicious = weekChecks.filter(c => c.verificationStatus === 'suspicious').length;
      
      stats.push({
        pumperId,
        pumperName: weekChecks[0]?.pumperName || pumperId,
        totalChecksToday: todayChecks.length,
        totalChecksThisWeek: weekChecks.length,
        wellsVisitedToday: wellsToday,
        averageTimePerWell: Math.round(avgTime),
        totalMilesToday: 0, // Would need route tracking to calculate
        verifiedChecks: verified,
        unverifiedChecks: unverified,
        suspiciousChecks: suspicious,
      });
    }
    
    return stats;
  }
  
  /**
   * Subscribe to real-time check updates (for manager dashboard)
   */
  subscribeToTodaysChecks(
    callback: (checks: WellCheck[]) => void
  ): () => void {
    const companyId = getCompanyId();
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    
    const q = query(
      collection(db, this.collectionName),
      where('companyId', '==', companyId),
      where('checkInTime', '>=', Timestamp.fromDate(startOfDay)),
      orderBy('checkInTime', 'desc'),
      limit(100)
    );
    
    return onSnapshot(q, (snapshot) => {
      const checks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        checkInTime: doc.data().checkInTime?.toDate(),
        checkOutTime: doc.data().checkOutTime?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as WellCheck[];
      
      callback(checks);
    });
  }
  
  /**
   * Calculate distance in feet between two coordinates
   */
  private calculateDistanceFeet(
    lat1: number, lng1: number,
    lat2: number, lng2: number
  ): number {
    const EARTH_RADIUS_FEET = 20902231; // feet
    
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_FEET * c;
  }
  
  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }
}

// Singleton export
export const wellCheckService = new WellCheckService();
export default wellCheckService;
